import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowRight,
  CheckCircle,
  Mail,
  Building2,
  MapPin,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import GovernanceFAQCallout from "@/components/GovernanceFAQCallout";
import { getGaClientId } from "@/lib/gaClientId";
import { getRecaptchaToken } from "@/lib/recaptcha";

const SERVICE_OPTIONS = [
  { value: "capa_deviation", label: "CAPA/Deviation Documentation" },
  { value: "validation_qualification", label: "Validation/Qualification Docs" },
  { value: "training_governance", label: "Training & Governance (Coming Soon)" },
  { value: "template_packs", label: "Template Packs (coming soon)" },
  { value: "not_sure", label: "Not sure yet — let's talk" },
];

const TEAM_SIZES = [
  { value: "1-5", label: "1–5" },
  { value: "6-15", label: "6–15" },
  { value: "16-50", label: "16–50" },
  { value: "50+", label: "50+" },
];

// Monthly plan titles (see MONTHLY_PLANS in pricingConfig.js) don't map 1:1 to a
// single service_interest — default them all to CAPA/Deviation, the most common
// entry-point workflow, so the dropdown pre-selects something useful rather than blank.
const PLAN_TO_SERVICE_INTEREST = {
  starter: "capa_deviation",
  professional: "capa_deviation",
  lab: "capa_deviation",
};
const PLAN_LABELS = { starter: "Starter", professional: "Professional", lab: "Lab" };

export default function Contact() {
  const planKey = (new URLSearchParams(window.location.search).get("plan") || "").toLowerCase();
  const planParam = PLAN_LABELS[planKey] || null;
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    team_size: "",
    service_interest: planParam ? PLAN_TO_SERVICE_INTEREST[planKey] : "",
    message: planParam ? `I'm interested in the ${planParam} monthly plan.` : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email to continue");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const recaptcha_token = await getRecaptchaToken("contact_form_submit");
      const res = await base44.functions.invoke('submitContactInquiry', { ...form, ga_client_id: getGaClientId(), recaptcha_token });
      if (res.data?.error) {
        toast.error(res.data.error);
        return;
      }
      setSubmitted(true);
      toast.success("Thank you! We'll reach out within 24 hours.");
      base44.analytics.track({ eventName: "contact_form_submitted", properties: { service_interest: form.service_interest || "unknown" } });
    } catch (error) {
      toast.error("Something went wrong. Please try again or email us directly at hello@labcomplyx.com");
      console.error("Contact form error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Contact LabComplyx — CLIA, FDA, and GxP Documentation Support for Regulated Labs"
        description="Need help with CLIA deficiencies, 483 responses, or SOP documentation? Reach Irvin Palmer at LabComplyx — most labs see results within one working day."
        keywords="contact LabComplyx, book compliance review, regulated lab documentation support, inspection readiness, QA workflow"
        canonical="https://labcomplyx.com/contact"
        ogUrl="https://labcomplyx.com/contact"
        breadcrumbs={[
          { name: "Home", url: "https://labcomplyx.com/" },
          { name: "Contact", url: "https://labcomplyx.com/Contact" },
        ]}
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact LabComplyx — CLIA, FDA, and GxP Documentation Support",
          "description": "Need help with CLIA deficiencies, 483 responses, or SOP documentation? Reach Irvin Palmer at LabComplyx — most labs see results within one working day.",
          "url": "https://labcomplyx.com/Contact",
          "publisher": { "@id": "https://labcomplyx.com/#organization" },
          "mainEntity": {
            "@type": "Organization",
            "@id": "https://labcomplyx.com/#organization",
            "name": "LabComplyx",
            "url": "https://labcomplyx.com",
            "email": "hello@labcomplyx.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "5540 Centerview Dr Ste 204",
              "addressLocality": "Raleigh",
              "addressRegion": "NC",
              "postalCode": "27606-8012",
              "addressCountry": "US"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": "hello@labcomplyx.com",
              "availableLanguage": "English",
              "areaServed": "US"
            }
          }
        }}
      />
      <div className="pt-28 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="lg:flex lg:gap-16">
            {/* Left info */}
            <div className="lg:w-5/12 mb-12 lg:mb-0">
              <span className="text-xs sm:text-sm font-medium text-secondary uppercase tracking-wider">
                 Get In Touch
                </span>
              <h1 className="mt-3">
                Book a Free Compliance Review
              </h1>
              {planParam && (
                <span className="inline-block mt-4 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                  Selected plan: {planParam}
                </span>
              )}
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                Start with a focused conversation about where your lab is feeling the most pressure — documentation backlog, inspection readiness, SOP maintenance, CAPAs, deviations, validation support, or quality workflow gaps.
              </p>

              <div className="mt-10 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">Email</h3>
                    <p className="text-sm text-muted-foreground">hello@labcomplyx.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">Mailing Address</h3>
                    <p className="text-sm text-muted-foreground">
                      LabComplyx<br />
                      5540 Centerview Dr Ste 204<br />
                      Raleigh, NC 27606-8012, US
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">Serving</h3>
                    <p className="text-sm text-muted-foreground">
                      Clinical & diagnostic, environmental testing, cannabis & hemp, and pharma/GxP labs — across the US and internationally.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 bg-accent/50 rounded-2xl p-6">
                <h4 className="text-sm font-semibold text-foreground mb-4">
                  What to Expect on Your Discovery Call:
                </h4>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-foreground">Process Assessment:</strong> We'll review your current CAPA/validation workflows and identify bottlenecks
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-foreground">AI Demo:</strong> See live examples of how our AI agents work in real documentation scenarios
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-foreground">Custom Roadmap:</strong> Walk away with a preliminary implementation plan and time-saving estimates
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-foreground">No Pressure:</strong> Actionable insights whether we work together or not
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-6">
                <GovernanceFAQCallout
                  heading="Need immediate compliance answers?"
                  body="We've built an evidence-based FAQ for QA and compliance teams covering our audit trail, AI validation protocols, regulatory traceability, and security posture."
                />
              </div>
            </div>

            {/* Right form */}
            <div className="lg:w-7/12">
              {submitted ? (
                <div className="bg-card rounded-3xl border border-border p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="w-8 h-8 text-secondary" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">
                    We received your inquiry
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Thank you for reaching out. We'll review your information and contact you 
                    within 24 hours to schedule your discovery call.
                  </p>
                </div>
              ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-card rounded-3xl border border-border p-8 lg:p-10 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-medium text-foreground mb-1.5">
                      Full Name *
                    </label>
                    <Input
                      required
                      placeholder="Jane Smith"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-foreground mb-1.5">
                      Work Email *
                    </label>
                    <Input
                      type="email"
                      required
                      placeholder="jane@lab.com"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-medium text-foreground mb-1.5">
                      Company / Lab
                    </label>
                    <Input
                      placeholder="Acme Diagnostics"
                      value={form.company}
                      onChange={(e) => handleChange("company", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-foreground mb-1.5">
                      Your Role
                    </label>
                    <Input
                      placeholder="e.g. QA Manager, Regulatory Affairs, Lab Director"
                      value={form.role}
                      onChange={(e) => handleChange("role", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-medium text-foreground mb-1.5">
                      QA Team Size
                    </label>
                    <Select
                      value={form.team_size}
                      onValueChange={(v) => handleChange("team_size", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        {TEAM_SIZES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block font-medium text-foreground mb-1.5">
                      Service Interest
                    </label>
                    <Select
                      value={form.service_interest}
                      onValueChange={(v) => handleChange("service_interest", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                   <label className="block font-medium text-foreground mb-1.5">
                     Tell Us More
                   </label>
                  <Textarea
                    placeholder="Describe your current documentation challenges, timeline, or any questions..."
                    rows={4}
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full gap-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Submit Inquiry
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Internal linking section — improves AI Search crawlability */}
      <div className="bg-muted/40 border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-5">Explore LabComplyx</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/solutions" className="text-primary hover:underline">Our Solutions</Link>
            <Link to="/solutions/capa-documentation" className="text-primary hover:underline">CAPA Documentation</Link>
            <Link to="/solutions/sop-management" className="text-primary hover:underline">SOP Management</Link>
            <Link to="/solutions/inspection-readiness" className="text-primary hover:underline">Inspection Readiness</Link>
            <Link to="/solutions/deviation-documentation" className="text-primary hover:underline">Deviation Documentation</Link>
            <Link to="/solutions/validation-support" className="text-primary hover:underline">Validation Support</Link>
            <Link to="/solutions#lab-types" className="text-primary hover:underline">Lab Types We Serve</Link>
            <Link to="/roi-calculator" className="text-primary hover:underline">ROI Calculator</Link>
            <Link to="/governance-faq" className="text-primary hover:underline">Governance &amp; Trust FAQ</Link>
            <Link to="/blog" className="text-primary hover:underline">Compliance Blog</Link>
            <Link to="/about" className="text-primary hover:underline">About LabComplyx</Link>
          </div>
        </div>
      </div>
    </>
  );
}